import React, { useState, useCallback } from 'react';
import { Button, Image, Modal } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import ShieldIcon from 'assets/images/shield-icon.svg';
import PINInput from 'components/common/PINInput';
import ResetPINModal from 'components/common/ResetPINModal';
import './styles.scss';
import useWindowSize from 'utils/useWindowSize';

const PINConfirmationModal = ({
  open,
  setOpen,
  cancelText,
  confirmText,
  onCancel,
  onPinConfirm,
  loading,
  onClose,
  setPIN,
  PIN,
  uploadProgress = undefined,
}) => {
  const windowSize = useWindowSize();

  const [openResetPIN, setOpenResetPIN] = useState(false);

  const handleOpenResetPIN = useCallback(() => {
    setOpenResetPIN(true);
    setPIN('');
  }, []);

  return (
    <>
      <Modal
        onClose={() => {
          setOpen(false);
          onClose();
          setPIN('');
          setOpenResetPIN(false);
        }}
        onOpen={() => {
          setPIN('');
          setOpen(true);
        }}
        open={open}
        size={windowSize.width > 600 ? 'tiny' : 'mini'}
        className="pin-modal"
        closeOnDimmerClick={false}
      >
        <div className="pin-modal__content">
          <div className="pin-modal__left-side">
            <div className="pin-modal__image-container">
              <Image src={ShieldIcon} className="pin-modal__image" />
            </div>
            <p className="pin-modal__description">
              {global.translate(
                'We keep your account and transactions safe.',
              )}
            </p>
          </div>
          <div className="pin-modal__right-side">
            <h1 className="pin-modal__heading">
              {global.translate('Enter PIN to confirm')}
            </h1>
            <p className="pin-modal__label">
              {global.translate('PIN number')}
            </p>
            <form autoComplete="off">
              <PINInput value={PIN} onChange={setPIN} length={6} />
            </form>
            <p className="pin-modal__reset">
              {global.translate('Forgot your PIN?')}
              <Button
                className="pin-modal__button pin-modal__button--reset"
                onClick={handleOpenResetPIN}
              >
                {global.translate('Reset')}
              </Button>
            </p>

            {uploadProgress !== undefined ? (
              <p style={{ color: '#a91f23' }}>
                {global.translate(
                  'Uploading data is in progress... ',
                )}{' '}
                {uploadProgress}
              </p>
            ) : null}

            <div className="pin-modal__actions">
              <Button
                className="pin-modal__button pin-modal__button--cancel"
                onClick={() => {
                  if (
                    typeof onCancel === 'function' &&
                    PIN?.length === 6
                  ) {
                    onCancel();
                  } else {
                    setPIN('');
                    setOpen(false);
                    onClose();
                  }
                }}
                disabled={loading}
              >
                {typeof onCancel === 'function' && PIN?.length < 6
                  ? global.translate('Cancel')
                  : cancelText || global.translate('Cancel')}
              </Button>
              <Button
                className="pin-modal__button pin-modal__button--confirm"
                onClick={() => {
                  onPinConfirm();
                }}
                disabled={PIN?.length < 6 || loading}
                loading={loading}
              >
                {confirmText || global.translate('Confirm')}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <ResetPINModal
        open={openResetPIN}
        setOpen={handleOpenResetPIN}
        close={() => setOpenResetPIN(false)}
      />
    </>
  );
};

PINConfirmationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  cancelText: PropTypes.string,
  confirmText: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onPinConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  onClose: PropTypes.func,
  PIN: PropTypes.string,
  setPIN: PropTypes.func.isRequired,
};

PINConfirmationModal.defaultProps = {
  loading: false,
  onClose: () => {},
  PIN: '',
};

export default PINConfirmationModal;
